import { ReactElement, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DeviceAttributesV1, DeviceType, Group } from '../constants/telosair-interfaces';
import StatusButton from './StatusButton';
import { getDisplayableValue } from '../_helpers/globalHelper';
import Loader from './loader';
import { getPriorityColor } from '../_helpers/dashboardHelper';
import { useData } from '../context/data.context';
import TruncatedText from './truncatedText';

/**
 * GetClasses function for finding the right class for icon based on priority of device.
 * @param priority - Priority of the device.
 * @returns - Class for the icon.
 */
const getClassesBasedOnPriority = (priority: string): string => {
  let classes = '';
  if (priority === 'high') {
    classes = 'text-error-500';
  } else if (priority === 'medium') {
    classes = 'text-success-500';
  } else if (priority === 'low') {
    classes = 'text-warning-500';
  } else {
    classes = 'text-white';
  }
  return classes;
};

type IndividualGroupProps = {
  isSelected: boolean;
  individualDevicedata: DeviceAttributesV1;
  setSelectedDevice: (device: DeviceAttributesV1) => void;
  selectedDevice: DeviceAttributesV1 | null;
  noSpacingLeft?: boolean;
};

/**
 * A IndividualGroup component for rendering IndividualGroup with its sensors data in a grid.
 * @param props - IndividualGroup props.
 * @returns A React element representing the IndividualGroup component.
 */
export function IndividualGroup(props: IndividualGroupProps): ReactElement {
  const { individualDevicedata, setSelectedDevice, selectedDevice, isSelected, noSpacingLeft } =
    props;
  const {
    clientThresholdSettings,
    deviceDataSummary,
    loading,
    parameters,
    devices,
    userConfigDataV2
  } = useData();

  const particleData = deviceDataSummary[individualDevicedata.serial_number]?.parameters || {};
  const deviceDetails = devices.filter(
    (item) => item.serialNumber === individualDevicedata.serial_number
  )[0];

  const deviceType =
    devices.find((item) => item.serialNumber === individualDevicedata.serial_number)?.deviceType ||
    DeviceType.MIXED;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={() => setSelectedDevice(individualDevicedata)}
      className={`bg-gray-100 dark:bg-container-800 flex text-gray-500 dark:text-gray-300 border-b border-b-gray-300 dark:border-b-gray-900 cursor-pointer border-l-primary-500 ${
        selectedDevice?.serial_number === individualDevicedata.serial_number
          ? 'border-l-4'
          : isSelected && 'border-l-2'
      }`}
    >
      <div
        className={`py-2 pr-8 flex font-medium items-center w-[22%] ${
          noSpacingLeft ? 'px-3' : 'px-6'
        }`}
      >
        <span
          className={`material-icons-outlined text-base mx-3 ${getClassesBasedOnPriority('low')}`}
        >
          memory
        </span>
        {'  '}
        <span className="text-sm">
          {deviceDetails.deviceName} (#{individualDevicedata.serial_number})
        </span>
      </div>
      {!loading ? (
        <div
          style={{ gridTemplateColumns: `repeat(${parameters.length}, minmax(0, 1fr))` }}
          className={`flex-1 grid text-center `}
        >
          {parameters.map((parameter) => {
            const value = particleData[parameter.parameterName];
            return (
              <div key={parameter.parameterName} className="p-2">
                <StatusButton
                  data={{
                    property_value: getDisplayableValue(parameter.parameterName, value),
                    priority: getPriorityColor(
                      parameter.parameterName,
                      value,
                      clientThresholdSettings,
                      userConfigDataV2,
                      deviceType
                    )
                  }}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="relative flex justify-center items-center flex-1">
          <Loader size={16} />
        </div>
      )}
    </div>
  );
}

type GroupProps = {
  groupDetails: Group;
  expandAll: boolean;
  onRowSelection: (data: number) => void;
  selectedGroup: number | null;
  index: number;
  setSelectedDevice: (device: DeviceAttributesV1) => void;
  selectedDevice: DeviceAttributesV1 | null;
};

/**
 * A Group Component will render list of devices it has.
 * @param props - Properties of Group component.
 * @returns - A react Element.
 */
export default function GroupComponent(props: GroupProps): ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    onRowSelection,
    groupDetails,
    selectedGroup,
    index,
    setSelectedDevice,
    expandAll,
    selectedDevice
  } = props;
  const [showMoreDevice, setShowMoreDevice] = useState(false);
  const [params] = useSearchParams({});
  const paramGroup = params.get('group');
  const { groupDataSummary, clientThresholdSettings, parameters, userConfigDataV2, groupTypes } =
    useData();
  useEffect(() => {
    if (expandAll) {
      setShowMoreDevice(true);
      return;
    }
    if (paramGroup && parseInt(paramGroup, 10) === groupDetails.groupId) {
      setShowMoreDevice(true);
      return;
    }
    if (selectedGroup === groupDetails.groupId) {
      setShowMoreDevice(true);
      return;
    }
    if (index === 0) {
      setShowMoreDevice(true);
      return;
    }
    setShowMoreDevice(false);
  }, [index, selectedGroup, groupDetails, expandAll]);

  /**
   * Function to toggle the showMoreDevice devices.
   * @param e - On click event.
   */
  const toggleShowMoreDevices = (e: any): void => {
    e.stopPropagation();
    setShowMoreDevice((prev) => !prev);
  };

  /**
   * Function to handle row click.
   */
  const onRowClick = (): void => {
    onRowSelection(groupDetails.groupId);
  };

  const particleData = groupDataSummary[groupDetails.groupId]?.parameters || {};

  // const AVAERAGE_VALUE = getGroupAverageValue(groupDetails);

  return (
    <>
      <div
        className={`bg-white dark:bg-container-500 flex text-gray-500 dark:text-gray-300 border-b border-b-gray-300 dark:border-b-gray-900 ${
          selectedGroup === groupDetails.groupId && 'border-l-primary-500 border-l-2'
        }`}
        role="button"
        tabIndex={0}
        onClick={onRowClick}
        onKeyDown={onRowClick}
      >
        <div className="px-6 py-4 pr-8 flex items-center w-[22%]">
          <span
            className={`material-icons-outlined text-sm ${getClassesBasedOnPriority('low')} mr-3`}
          >
            api
          </span>
          <TruncatedText text={groupDetails.groupName} />
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              toggleShowMoreDevices(e);
            }}
            onKeyDown={(e) => toggleShowMoreDevices(e)}
            className="text-xs font-normal"
          >
            <span className="material-icons-outlined text-md pl-0.5 cursor-pointer  text-center">
              {!showMoreDevice ? 'expand_more' : 'expand_less'}
            </span>
          </div>
        </div>
        <div
          style={{ gridTemplateColumns: `repeat(${parameters.length}, minmax(0, 1fr))` }}
          className={`flex-1 grid text-center `}
        >
          {parameters.map((parameter) => {
            const value = particleData[parameter.parameterName];
            return (
              <div key={parameter.parameterName} className="p-2">
                <StatusButton
                  data={{
                    property_value: getDisplayableValue(parameter.parameterName, value),
                    priority: getPriorityColor(
                      parameter.parameterName,
                      value,
                      clientThresholdSettings,
                      userConfigDataV2,
                      groupTypes[groupDetails.groupId] || DeviceType.MIXED
                    )
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
      {showMoreDevice ? (
        <div>
          {groupDetails?.sensorDetailsList?.map((currentDevice) => (
            <IndividualGroup
              isSelected={selectedGroup === groupDetails.groupId}
              key={currentDevice.id}
              individualDevicedata={currentDevice}
              setSelectedDevice={setSelectedDevice}
              selectedDevice={selectedDevice}
            />
          ))}
        </div>
      ) : (
        ''
      )}
    </>
  );
}
